// ============================================================
// 📁 آدرس این فایل در پروژه:
//    src/pages/hr/EmployeeProfile.tsx
// ============================================================

import { useState, useEffect, useRef } from "react";
import { useTheme, DARK, LIGHT } from "../../components/Layout";
import { hrAPI, uploadAPI, accountsAPI, journalAPI } from "../../services/api";
import SearchableSelect from "../../components/SearchableSelect";
import PersianDatePicker, { gregorianToJalaliStr, todayGregorian } from "../../utils/PersianDatePicker";
import {
  ArrowRight, Edit2, Save, X, Plus, Trash2, FileText, Camera,
  ArrowDownCircle, ArrowUpCircle,
} from "lucide-react";

interface Props { employeeId: number; onBack: () => void; }

const fmt = (n: any) => new Intl.NumberFormat("en-US").format(Number(n) || 0);
const currencies = ["AFN", "USD", "IRR", "TRY", "PKR", "AED", "EUR"];

type Tab = "info" | "transactions";

export default function EmployeeProfile({ employeeId, onBack }: Props) {
  const { dark } = useTheme();
  const T = dark ? DARK : LIGHT;
  const photoRef = useRef<HTMLInputElement>(null);
  const [tab, setTab] = useState<Tab>("info");
  const [data, setData] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [toast, setToast] = useState<{ msg: string; ok: boolean } | null>(null);
  const [editForm, setEditForm] = useState<any>({});

  // حساب‌های نقدی شرکت (برای انتخاب حساب پرداختی/دریافتی)
  const [cashAccounts, setCashAccounts] = useState<any[]>([]);
  // دسته‌بندی‌های واقعی مصارف (همان دسته‌بندی‌های تب «ثبت مصارف» در روزنامچه)
  const [expenseCats, setExpenseCats] = useState<any[]>([]);

  // تراکنش‌ها
  const [transactions, setTransactions] = useState<any[]>([]);
  const [txStats, setTxStats] = useState<{ pay: number; receive: number; balance: number; old_loan_balance: number }>({ pay: 0, receive: 0, balance: 0, old_loan_balance: 0 });
  const [showTxForm, setShowTxForm] = useState(false);
  const [editingTx, setEditingTx] = useState<any>(null);
  const [txForm, setTxForm] = useState({
    type: "pay" as "pay" | "receive",
    category: "قرضه",
    category_id: "",
    category_name: "",
    amount: "",
    currency: "AFN",
    date: todayGregorian(),
    description: "",
    account_id: "",
    account_name: "",
    exchange_rate: "1",
  });

  const showToast = (msg: string, ok = true) => { setToast({ msg, ok }); setTimeout(() => setToast(null), 3500); };

  const load = async () => {
    setLoading(true);
    try {
      const r = await hrAPI.employeeGet(employeeId);
      if (r.success) {
        setData(r);
        setEditForm(r.employee);
      }
    } catch { showToast("خطا در اتصال به سرور", false); }
    setLoading(false);
  };

  const loadTx = async () => {
    try {
      const r = await hrAPI.txList(employeeId);
      if (r.success) {
        setTransactions(r.transactions || []);
        setTxStats({ pay: r.total_pay, receive: r.total_receive, balance: r.balance, old_loan_balance: r.old_loan_balance || 0 });
      }
    } catch { }
  };

  useEffect(() => { load(); }, [employeeId]);
  useEffect(() => { if (tab === "transactions") loadTx(); }, [tab, employeeId]);
  useEffect(() => {
    accountsAPI.getCashAccounts().then(r => { if (r.success) setCashAccounts(r.accounts || []); });
    journalAPI.getExpenseCats().then(r => { if (r.success) setExpenseCats(r.categories || []); });
  }, []);

  // آپلود عکس
  const handlePhotoUpload = async (file: File) => {
    setUploading(true);
    try {
      const r = await uploadAPI.uploadPhoto(file);
      if (r.success && r.url) {
        setEditForm((f: any) => ({ ...f, photo_url: r.url }));
        showToast("عکس آپلود شد");
      } else showToast(r.message || "خطا در آپلود", false);
    } catch { showToast("خطا در آپلود عکس", false); }
    setUploading(false);
  };

  // ذخیره اطلاعات کارمند
  const saveEmployee = async () => {
    setSaving(true);
    try {
      const r = await hrAPI.employeeUpdate(editForm);
      if (r.success) { showToast("اطلاعات ذخیره شد"); setEditing(false); load(); }
      else showToast(r.message || "خطا", false);
    } catch { showToast("خطا در اتصال به سرور", false); }
    setSaving(false);
  };

  // ────────── دسته‌بندی‌های مصارف (دقیقاً مثل تب «ثبت مصارف») ──────────
  const expenseCatOptions = () => {
    const opts: { value: string; label: string }[] = [{ value: "", label: "— انتخاب دسته‌بندی —" }];
    expenseCats.filter((c: any) => !c.parent_id).forEach((p: any) => {
      opts.push({ value: String(p.id), label: p.name });
      expenseCats.filter((c: any) => c.parent_id === p.id).forEach((ch: any) => {
        opts.push({ value: String(ch.id), label: `    └ ${ch.name}` });
      });
    });
    return opts;
  };

  // دسته «قرضه» جدا از بقیه است (اصلاً مصرف نیست)؛ بقیه از دسته‌بندی واقعی مصارف انتخاب می‌شوند
  const isLoan = txForm.category === "قرضه";

  // ────────── تراکنش‌ها ──────────
  const resetTxForm = () => {
    setTxForm({
      type: "pay",
      category: "قرضه",
      category_id: "",
      category_name: "",
      amount: "",
      currency: "AFN",
      date: todayGregorian(),
      description: "",
      account_id: "",
      account_name: "",
      exchange_rate: "1",
    });
    setEditingTx(null);
  };

  const openAddTx = () => {
    resetTxForm();
    setShowTxForm(true);
  };

  const openEditTx = (tx: any) => {
    setEditingTx(tx);
    setTxForm({
      type: tx.type,
      category: tx.category || "قرضه",
      // برای مصارف واقعی، دسته‌بندی باید دوباره انتخاب شود (شناسه دسته در تراکنش‌های کارمند ذخیره نمی‌شود)
      category_id: "",
      category_name: "",
      amount: String(tx.amount),
      currency: tx.currency || "AFN",
      date: tx.date,
      description: tx.description || "",
      account_id: tx.account_id ? String(tx.account_id) : "",
      account_name: tx.account_name || "",
      exchange_rate: "1",
    });
    setShowTxForm(true);
  };

  const submitTx = async () => {
    const amount = parseFloat(txForm.amount);
    if (!amount || amount <= 0) { showToast("مبلغ نامعتبر است", false); return; }
    if (!txForm.date) { showToast("تاریخ الزامی است", false); return; }
    if (!txForm.account_id) { showToast("انتخاب حساب دریافتی/پرداختی الزامی است", false); return; }
    if (!isLoan && !txForm.category_id) { showToast("انتخاب دسته‌بندی مصارف الزامی است", false); return; }
    if (!isLoan && txForm.type !== "pay") { showToast("فقط «قرضه» می‌تواند دریافت باشد", false); return; }
    if (isLoan && (!txForm.exchange_rate || parseFloat(txForm.exchange_rate) <= 0)) { showToast("نرخ تبدیل الزامی است", false); return; }

    const payload = {
      employee_id: employeeId,
      type: txForm.type,
      category: txForm.category,
      category_id: isLoan ? null : txForm.category_id,
      amount,
      currency: txForm.currency,
      date: txForm.date,
      description: txForm.description,
      account_id: parseInt(txForm.account_id),
      account_name: txForm.account_name,
      exchange_rate: isLoan ? parseFloat(txForm.exchange_rate) : 1,
    };

    try {
      const r = editingTx
        ? await hrAPI.txUpdate({ id: editingTx.id, ...payload })
        : await hrAPI.txAdd(payload);
      if (r.success) {
        showToast(editingTx ? "تراکنش ویرایش شد" : "تراکنش ثبت شد");
        setShowTxForm(false);
        resetTxForm();
        loadTx();
      } else showToast(r.message || "خطا", false);
    } catch { showToast("خطا در اتصال به سرور", false); }
  };

  const deleteTx = async (id: number) => {
    if (!window.confirm("آیا مطمئن هستید این تراکنش حذف شود؟")) return;
    try {
      const r = await hrAPI.txDelete(id);
      if (r.success) { showToast("تراکنش حذف شد"); loadTx(); }
      else showToast(r.message || "خطا", false);
    } catch { showToast("خطا در اتصال به سرور", false); }
  };

  // ─────────── استایل‌ها ───────────
  const card: React.CSSProperties = {
    borderRadius: 16, padding: 24,
    background: dark ? "rgba(255,255,255,0.03)" : "#fff",
    border: `1px solid ${T.cardBorder}`,
    marginBottom: 16,
  };

  const inp: React.CSSProperties = {
    width: "100%", padding: "9px 12px",
    background: dark ? "rgba(255,255,255,0.05)" : "#f8faff",
    border: `1.5px solid ${dark ? "rgba(255,255,255,0.08)" : "rgba(0,0,0,0.08)"}`,
    borderRadius: 8, color: T.text, fontSize: 13,
    fontFamily: "'Vazirmatn','Segoe UI',sans-serif",
    direction: "rtl" as const, outline: "none", boxSizing: "border-box" as const,
  };

  const lbl: React.CSSProperties = { fontSize: 11, fontWeight: 700, color: T.subtext, display: "block", marginBottom: 5 };

  if (loading) {
    return (
      <div style={{ padding: 60, textAlign: "center", color: T.subtext, fontFamily: "inherit" }} dir="rtl">
        <div style={{ width: 40, height: 40, border: "3px solid rgba(139,92,246,0.2)", borderTop: "3px solid #8b5cf6", borderRadius: "50%", animation: "spin 0.8s linear infinite", margin: "0 auto 16px" }} />
        در حال بارگذاری...
        <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
      </div>
    );
  }

  if (!data) return null;
  const e = data.employee;

  return (
    <div style={{ padding: "28px 28px 40px", fontFamily: "'Vazirmatn','Segoe UI',sans-serif" }} dir="rtl">
      {toast && (
        <div style={{
          position: "fixed", top: 20, left: "50%", transform: "translateX(-50%)", zIndex: 9999,
          padding: "12px 24px", borderRadius: 12,
          background: dark ? "#1e293b" : "#fff",
          border: `2px solid ${toast.ok ? "#10b981" : "#ef4444"}`,
          boxShadow: "0 8px 32px rgba(0,0,0,0.25)",
          color: toast.ok ? "#10b981" : "#ef4444",
          fontSize: 13, fontWeight: 700,
        }}>{toast.msg}</div>
      )}

      {/* هدر */}
      <div style={{ display: "flex", alignItems: "center", gap: 14, marginBottom: 20 }}>
        <button onClick={onBack} style={{ width: 38, height: 38, borderRadius: 10, border: `1px solid ${T.cardBorder}`, background: "transparent", color: T.text, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center" }}>
          <ArrowRight size={18} />
        </button>
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: 11, color: "#8b5cf6", fontWeight: 700, letterSpacing: "1px", marginBottom: 4 }}>کارمند</div>
          <h1 style={{ fontSize: 22, fontWeight: 900, margin: 0, color: T.text }}>{e.first_name} {e.last_name}</h1>
          <div style={{ fontSize: 12, color: T.subtext, marginTop: 4 }}>{e.position || "—"} • {e.department || "—"} • کد: <span style={{ fontFamily: "monospace", color: "#8b5cf6", fontWeight: 700 }}>{e.code}</span></div>
        </div>
      </div>

      {/* تب‌ها */}
      <div style={{ display: "flex", gap: 0, marginBottom: 20, borderBottom: `1px solid ${T.cardBorder}` }}>
        {([["info", "👤 اطلاعات"], ["transactions", "💰 تراکنش‌ها"]] as [Tab, string][]).map(([id, label]) => (
          <button key={id} onClick={() => setTab(id)} style={{
            padding: "10px 22px", border: "none", background: "transparent", cursor: "pointer",
            fontSize: 13, fontWeight: 700, fontFamily: "inherit",
            color: tab === id ? "#8b5cf6" : T.subtext,
            borderBottom: tab === id ? "2px solid #8b5cf6" : "2px solid transparent",
            marginBottom: -1, transition: "all 0.2s",
          }}>{label}</button>
        ))}
      </div>

      {/* ───── تب اطلاعات ───── */}
      {tab === "info" && (
        <div style={card}>
          {/* عکس و نام */}
          <div style={{ display: "flex", alignItems: "center", gap: 18, marginBottom: 22 }}>
            <div style={{ position: "relative" }}>
              <div style={{
                width: 90, height: 90, borderRadius: 18,
                background: "linear-gradient(135deg,#8b5cf6,#6d28d9)",
                display: "flex", alignItems: "center", justifyContent: "center",
                fontSize: 28, color: "white", fontWeight: 900, overflow: "hidden",
              }}>
                {(editing ? editForm.photo_url : e.photo_url) ? (
                  <img src={editing ? editForm.photo_url : e.photo_url} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                ) : `${e.first_name?.[0]}${e.last_name?.[0]}`}
              </div>
              {editing && (
                <button onClick={() => photoRef.current?.click()} disabled={uploading} style={{
                  position: "absolute", bottom: -4, right: -4,
                  width: 28, height: 28, borderRadius: "50%",
                  background: "#8b5cf6", color: "white", border: "2px solid #fff",
                  cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center",
                }}>
                  <Camera size={13} />
                </button>
              )}
              <input ref={photoRef} type="file" accept="image/*" style={{ display: "none" }} onChange={ev => { const f = ev.target.files?.[0]; if (f) handlePhotoUpload(f); }} />
            </div>
            <div style={{ flex: 1 }}>
              <h2 style={{ margin: 0, fontSize: 19, fontWeight: 800, color: T.text }}>{e.first_name} {e.last_name}</h2>
              <div style={{ fontSize: 13, color: T.subtext, marginTop: 4 }}>پدر: {e.father_name || "—"}</div>
            </div>
            {editing ? (
              <div style={{ display: "flex", gap: 8 }}>
                <button onClick={() => { setEditing(false); setEditForm(e); }} style={{ padding: "8px 14px", borderRadius: 8, border: `1px solid ${T.cardBorder}`, background: "transparent", color: T.text, cursor: "pointer", fontSize: 12, fontFamily: "inherit", display: "flex", alignItems: "center", gap: 4 }}>
                  <X size={13} /> لغو
                </button>
                <button onClick={saveEmployee} disabled={saving} style={{ padding: "8px 18px", borderRadius: 8, border: "none", background: saving ? "#64748b" : "linear-gradient(135deg,#10b981,#059669)", color: "white", cursor: "pointer", fontSize: 12, fontFamily: "inherit", fontWeight: 700, display: "flex", alignItems: "center", gap: 4 }}>
                  <Save size={13} /> ذخیره
                </button>
              </div>
            ) : (
              <button onClick={() => setEditing(true)} style={{ padding: "8px 18px", borderRadius: 8, border: "none", background: "linear-gradient(135deg,#8b5cf6,#6d28d9)", color: "white", cursor: "pointer", fontSize: 12, fontFamily: "inherit", fontWeight: 700, display: "flex", alignItems: "center", gap: 4 }}>
                <Edit2 size={13} /> ویرایش
              </button>
            )}
          </div>

          {/* فیلدها */}
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 14 }}>
            <Field label="نام" value={editing ? editForm.first_name : e.first_name} editing={editing} onChange={v => setEditForm({ ...editForm, first_name: v })} />
            <Field label="تخلص" value={editing ? editForm.last_name : e.last_name} editing={editing} onChange={v => setEditForm({ ...editForm, last_name: v })} />
            <Field label="نام پدر" value={editing ? editForm.father_name : e.father_name} editing={editing} onChange={v => setEditForm({ ...editForm, father_name: v })} />
            <Field label="شماره تذکره" value={editing ? editForm.national_id : e.national_id} editing={editing} onChange={v => setEditForm({ ...editForm, national_id: v })} />
            <Field label="تاریخ تولد" value={editing ? editForm.birth_date : (e.birth_date ? gregorianToJalaliStr(e.birth_date) : "")} editing={editing} onChange={v => setEditForm({ ...editForm, birth_date: v })} type="date" />
            <Field label="جنسیت" value={editing ? editForm.gender : (e.gender === "male" ? "مرد" : e.gender === "female" ? "زن" : "—")} editing={editing} onChange={v => setEditForm({ ...editForm, gender: v })} type="select" options={[{ value: "male", label: "مرد" }, { value: "female", label: "زن" }]} />
            <Field label="شماره تماس" value={editing ? editForm.phone : e.phone} editing={editing} onChange={v => setEditForm({ ...editForm, phone: v })} />
            <Field label="شماره دوم" value={editing ? editForm.phone2 : e.phone2} editing={editing} onChange={v => setEditForm({ ...editForm, phone2: v })} />
            <Field label="ولایت" value={editing ? editForm.province : e.province} editing={editing} onChange={v => setEditForm({ ...editForm, province: v })} />
            <div style={{ gridColumn: "1/-1" }}>
              <Field label="آدرس" value={editing ? editForm.address : e.address} editing={editing} onChange={v => setEditForm({ ...editForm, address: v })} />
            </div>
            <Field label="وظیفه" value={editing ? editForm.position : e.position} editing={editing} onChange={v => setEditForm({ ...editForm, position: v })} />
            <Field label="بخش" value={editing ? editForm.department : e.department} editing={editing} onChange={v => setEditForm({ ...editForm, department: v })} />
            <Field label="تحصیلات" value={editing ? editForm.education : e.education} editing={editing} onChange={v => setEditForm({ ...editForm, education: v })} />
            <div style={{ gridColumn: "1/-1" }}>
              <Field label="یادداشت" value={editing ? editForm.notes : e.notes} editing={editing} onChange={v => setEditForm({ ...editForm, notes: v })} type="textarea" />
            </div>
          </div>
        </div>
      )}

      {/* ───── تب تراکنش‌ها ───── */}
      {tab === "transactions" && (
        <>
          {/* خلاصه بیلانس */}
          <div style={{ display: "grid", gridTemplateColumns: txStats.old_loan_balance > 0 ? "1fr 1fr 1fr 1fr" : "1fr 1fr 1fr", gap: 14, marginBottom: 16 }}>
            <div style={{ ...card, marginBottom: 0, borderTop: "3px solid #10b981" }}>
              <div style={{ fontSize: 11, color: T.subtext, fontWeight: 700, marginBottom: 8 }}>📤 جمع پرداخت‌ها به کارمند</div>
              <div style={{ fontSize: 22, fontWeight: 900, color: "#10b981", direction: "ltr" as const }}>{fmt(txStats.pay)}</div>
            </div>
            <div style={{ ...card, marginBottom: 0, borderTop: "3px solid #f59e0b" }}>
              <div style={{ fontSize: 11, color: T.subtext, fontWeight: 700, marginBottom: 8 }}>📥 جمع دریافت‌ها از کارمند</div>
              <div style={{ fontSize: 22, fontWeight: 900, color: "#f59e0b", direction: "ltr" as const }}>{fmt(txStats.receive)}</div>
            </div>
            {txStats.old_loan_balance > 0 && (
              <div style={{ ...card, marginBottom: 0, borderTop: "3px solid #8b5cf6" }}>
                <div style={{ fontSize: 11, color: T.subtext, fontWeight: 700, marginBottom: 8 }}>📜 قرضه‌های قدیمی</div>
                <div style={{ fontSize: 22, fontWeight: 900, color: "#8b5cf6", direction: "ltr" as const }}>{fmt(txStats.old_loan_balance)}</div>
                <div style={{ fontSize: 10, color: T.subtext, marginTop: 4 }}>(از سیستم قبلی)</div>
              </div>
            )}
            <div style={{ ...card, marginBottom: 0, borderTop: `3px solid ${txStats.balance > 0 ? "#ef4444" : txStats.balance < 0 ? "#10b981" : "#64748b"}` }}>
              <div style={{ fontSize: 11, color: T.subtext, fontWeight: 700, marginBottom: 8 }}>⚖️ بیلانس کارمند</div>
              <div style={{ fontSize: 22, fontWeight: 900, color: txStats.balance > 0 ? "#ef4444" : txStats.balance < 0 ? "#10b981" : T.text, direction: "ltr" as const }}>
                {fmt(Math.abs(txStats.balance))}
              </div>
              <div style={{ fontSize: 10, color: T.subtext, marginTop: 4 }}>
                {txStats.balance > 0 ? "کارمند قرضدار است" : txStats.balance < 0 ? "ما قرضدار کارمند هستیم" : "تسویه است"}
              </div>
            </div>
          </div>

          {/* فرم ثبت / ویرایش تراکنش */}
          {showTxForm && (
            <div style={{ ...card, borderRight: `4px solid ${txForm.type === "pay" ? "#10b981" : "#f59e0b"}` }}>
              <div style={{ fontSize: 14, fontWeight: 800, color: T.text, marginBottom: 16 }}>
                {editingTx ? "✏️ ویرایش تراکنش" : "➕ ثبت تراکنش جدید"}
              </div>

              {/* انتخاب دسته — قرضه یا مصرف واقعی */}
              <div style={{ marginBottom: 14 }}>
                <label style={lbl}>نوع تراکنش</label>
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
                  <button onClick={() => setTxForm(f => ({ ...f, category: "قرضه", category_id: "", category_name: "" }))} style={{
                    padding: "12px", borderRadius: 10,
                    border: isLoan ? "2px solid #8b5cf6" : `1px solid ${T.cardBorder}`,
                    background: isLoan ? "rgba(139,92,246,0.08)" : "transparent",
                    color: isLoan ? "#8b5cf6" : T.text,
                    cursor: "pointer", fontSize: 13, fontWeight: 700, fontFamily: "inherit",
                  }}>💰 قرضه به کارمند</button>
                  <button onClick={() => setTxForm(f => ({ ...f, category: "سایر", type: "pay", category_id: "", category_name: "" }))} style={{
                    padding: "12px", borderRadius: 10,
                    border: !isLoan ? "2px solid #ef4444" : `1px solid ${T.cardBorder}`,
                    background: !isLoan ? "rgba(239,68,68,0.08)" : "transparent",
                    color: !isLoan ? "#ef4444" : T.text,
                    cursor: "pointer", fontSize: 13, fontWeight: 700, fontFamily: "inherit",
                  }}>🧾 مصرف (پاداش/اضافه‌کاری/...)</button>
                </div>
              </div>

              {/* انتخاب پرداخت/دریافت — فقط برای قرضه */}
              {isLoan && (
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, marginBottom: 14 }}>
                  <button onClick={() => setTxForm(f => ({ ...f, type: "pay" }))} style={{
                    padding: "12px", borderRadius: 10,
                    border: txForm.type === "pay" ? "2px solid #10b981" : `1px solid ${T.cardBorder}`,
                    background: txForm.type === "pay" ? "rgba(16,185,129,0.08)" : "transparent",
                    color: txForm.type === "pay" ? "#10b981" : T.text,
                    cursor: "pointer", fontSize: 13, fontWeight: 700, fontFamily: "inherit",
                    display: "flex", alignItems: "center", justifyContent: "center", gap: 8,
                  }}>
                    <ArrowDownCircle size={16} /> پرداخت قرضه به کارمند
                  </button>
                  <button onClick={() => setTxForm(f => ({ ...f, type: "receive" }))} style={{
                    padding: "12px", borderRadius: 10,
                    border: txForm.type === "receive" ? "2px solid #f59e0b" : `1px solid ${T.cardBorder}`,
                    background: txForm.type === "receive" ? "rgba(245,158,11,0.08)" : "transparent",
                    color: txForm.type === "receive" ? "#f59e0b" : T.text,
                    cursor: "pointer", fontSize: 13, fontWeight: 700, fontFamily: "inherit",
                    display: "flex", alignItems: "center", justifyContent: "center", gap: 8,
                  }}>
                    <ArrowUpCircle size={16} /> بازپرداخت از کارمند
                  </button>
                </div>
              )}

              {!isLoan && (
                <div style={{
                  marginBottom: 14, padding: "8px 12px", borderRadius: 8,
                  background: dark ? "rgba(239,68,68,0.08)" : "#fef2f2",
                  border: `1px solid ${dark ? "rgba(239,68,68,0.2)" : "#fecaca"}`,
                  fontSize: 11, color: dark ? "#fca5a5" : "#b91c1c",
                }}>
                  این یک «ثبت مصارف» واقعی و غیرقابل‌برگشت در روزنامچه است.
                </div>
              )}

              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 12 }}>
                {!isLoan && (
                  <div style={{ gridColumn: "1/-1" }}>
                    <label style={lbl}>دسته‌بندی مصارف *</label>
                    <SearchableSelect
                      options={expenseCatOptions()}
                      value={txForm.category_id}
                      onChange={(v: string) => {
                        const cat = expenseCats.find((c: any) => String(c.id) === v);
                        setTxForm(f => ({ ...f, category_id: v, category_name: cat?.name || "", category: cat?.name || "سایر" }));
                      }}
                      placeholder="انتخاب دسته‌بندی"
                    />
                  </div>
                )}
                <div>
                  <label style={lbl}>مبلغ *</label>
                  <input type="number" value={txForm.amount} onChange={e => setTxForm(f => ({ ...f, amount: e.target.value }))} placeholder="0" style={{ ...inp, direction: "ltr" as const, fontWeight: 700, fontSize: 15 }} />
                </div>
                <div>
                  <label style={lbl}>ارز</label>
                  <select value={txForm.currency} onChange={e => setTxForm(f => ({ ...f, currency: e.target.value }))} style={{ ...inp, background: dark ? "#1e293b" : "#f8faff" }}>
                    {currencies.map(c => <option key={c} value={c}>{c}</option>)}
                  </select>
                </div>
                <div>
                  <PersianDatePicker label="تاریخ *" value={txForm.date} onChange={v => setTxForm(f => ({ ...f, date: v }))} />
                </div>
                {isLoan && (
                  <div>
                    <label style={lbl}>نرخ تبدیل *</label>
                    <input type="number" value={txForm.exchange_rate} onChange={e => setTxForm(f => ({ ...f, exchange_rate: e.target.value }))} placeholder="1" style={{ ...inp, direction: "ltr" as const }} />
                  </div>
                )}
                <div style={{ gridColumn: "1/-1" }}>
                  <label style={lbl}>حساب {txForm.type === "pay" ? "پرداختی (از کدام حساب کم شود)" : "دریافتی (به کدام حساب اضافه شود)"} *</label>
                  <SearchableSelect
                    options={cashAccounts.map((a: any) => ({ value: String(a.id), label: `${a.code} — ${a.name} (${a.currency})` }))}
                    value={txForm.account_id}
                    onChange={(v: string) => {
                      const acc = cashAccounts.find((a: any) => String(a.id) === v);
                      setTxForm(f => ({ ...f, account_id: v, account_name: acc?.name || "", currency: acc?.currency || f.currency }));
                    }}
                    placeholder="جستجو و انتخاب حساب..."
                  />
                </div>
                <div style={{ gridColumn: "1/-1" }}>
                  <label style={lbl}>شرح</label>
                  <input value={txForm.description} onChange={e => setTxForm(f => ({ ...f, description: e.target.value }))} placeholder="مثلاً: قرضه برای خرید موترسایکل، پاداش عملکرد، ..." style={inp} />
                </div>
              </div>

              <div style={{ display: "flex", gap: 8, marginTop: 16, justifyContent: "flex-end" }}>
                <button onClick={() => { setShowTxForm(false); resetTxForm(); }} style={{ padding: "9px 16px", borderRadius: 9, border: `1px solid ${T.cardBorder}`, background: "transparent", color: T.text, cursor: "pointer", fontSize: 12, fontFamily: "inherit", display: "flex", alignItems: "center", gap: 4 }}>
                  <X size={13} /> انصراف
                </button>
                <button onClick={submitTx} style={{ padding: "9px 18px", borderRadius: 9, border: "none", background: "linear-gradient(135deg,#8b5cf6,#6d28d9)", color: "white", cursor: "pointer", fontSize: 12, fontFamily: "inherit", fontWeight: 700, display: "flex", alignItems: "center", gap: 4 }}>
                  <Save size={13} /> {editingTx ? "ذخیره تغییرات" : "ثبت تراکنش"}
                </button>
              </div>
            </div>
          )}

          {/* لیست تراکنش‌ها */}
          <div style={card}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 14 }}>
              <div style={{ fontSize: 14, fontWeight: 800, color: T.text }}>📋 تاریخچه تراکنش‌ها ({transactions.length})</div>
              {!showTxForm && (
                <button onClick={openAddTx} style={{ padding: "8px 16px", borderRadius: 9, border: "none", background: "linear-gradient(135deg,#8b5cf6,#6d28d9)", color: "white", cursor: "pointer", fontSize: 12, fontFamily: "inherit", fontWeight: 700, display: "flex", alignItems: "center", gap: 4 }}>
                  <Plus size={13} /> تراکنش جدید
                </button>
              )}
            </div>

            {transactions.length === 0 ? (
              <div style={{ textAlign: "center", padding: 40, color: T.subtext }}>
                <div style={{ fontSize: 36, marginBottom: 8 }}>💸</div>
                هیچ تراکنشی ثبت نشده است
              </div>
            ) : (
              <div style={{ borderRadius: 10, overflow: "hidden", border: `1px solid ${T.cardBorder}` }}>
                <div style={{
                  display: "grid", gridTemplateColumns: "100px 100px 100px 1fr 130px 80px",
                  gap: 8, padding: "10px 14px",
                  background: dark ? "rgba(0,0,0,0.2)" : "rgba(0,0,0,0.03)",
                  fontSize: 11, fontWeight: 700, color: T.subtext, borderBottom: `1px solid ${T.cardBorder}`,
                }}>
                  <span>تاریخ</span>
                  <span>نوع</span>
                  <span>دسته</span>
                  <span>شرح</span>
                  <span style={{ textAlign: "left" }}>مبلغ</span>
                  <span style={{ textAlign: "center" }}>عملیات</span>
                </div>
                {transactions.map((tx, i) => (
                  <div key={tx.id} style={{
                    display: "grid", gridTemplateColumns: "100px 100px 100px 1fr 130px 80px",
                    gap: 8, padding: "12px 14px",
                    borderTop: i > 0 ? `1px solid ${T.cardBorder}` : "none",
                    alignItems: "center",
                    background: i % 2 === 0 ? "transparent" : (dark ? "rgba(255,255,255,0.01)" : "rgba(0,0,0,0.01)"),
                  }}>
                    <span style={{ fontSize: 11, color: T.subtext, direction: "ltr" as const }}>{gregorianToJalaliStr(tx.date)}</span>
                    <span style={{ fontSize: 11, fontWeight: 700, color: tx.type === "pay" ? "#10b981" : "#f59e0b" }}>
                      {tx.type === "pay" ? "📤 پرداخت" : "📥 دریافت"}
                    </span>
                    <span style={{ fontSize: 11, color: T.text }}>{tx.category}</span>
                    <span style={{ fontSize: 12, color: T.text }}>{tx.description || "—"}</span>
                    <span style={{
                      textAlign: "left", fontSize: 13, fontWeight: 800,
                      color: tx.type === "pay" ? "#10b981" : "#f59e0b",
                      direction: "ltr" as const,
                    }}>
                      {tx.type === "pay" ? "+" : "-"}{fmt(tx.amount)} {tx.currency}
                    </span>
                    <div style={{ display: "flex", gap: 4, justifyContent: "center" }}>
                      <button onClick={() => openEditTx(tx)} title="ویرایش" style={{ width: 28, height: 28, borderRadius: 6, border: "1px solid #3b82f6", background: "transparent", color: "#3b82f6", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center" }}>
                        <Edit2 size={11} />
                      </button>
                      <button onClick={() => deleteTx(tx.id)} title="حذف" style={{ width: 28, height: 28, borderRadius: 6, border: "1px solid #ef4444", background: "transparent", color: "#ef4444", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center" }}>
                        <Trash2 size={11} />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </>
      )}

      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );
}

// کامپوننت کمکی برای فیلدهای اطلاعات
function Field({ label, value, editing, onChange, type, options }: { label: string; value: any; editing: boolean; onChange: (v: string) => void; type?: "text" | "date" | "select" | "textarea"; options?: { value: string; label: string }[] }) {
  const { dark } = useTheme();
  const T = dark ? DARK : LIGHT;
  const inp: React.CSSProperties = {
    width: "100%", padding: "8px 12px",
    background: dark ? "rgba(255,255,255,0.05)" : "#f8faff",
    border: `1.5px solid ${dark ? "rgba(255,255,255,0.08)" : "rgba(0,0,0,0.08)"}`,
    borderRadius: 8, color: T.text, fontSize: 12,
    fontFamily: "'Vazirmatn','Segoe UI',sans-serif",
    direction: "rtl" as const, outline: "none", boxSizing: "border-box" as const,
  };
  return (
    <div>
      <div style={{ fontSize: 10, fontWeight: 700, color: T.subtext, marginBottom: 4 }}>{label}</div>
      {editing ? (
        type === "date" ? (
          <PersianDatePicker value={value} onChange={onChange} />
        ) : type === "select" ? (
          <select value={value} onChange={e => onChange(e.target.value)} style={{ ...inp, background: dark ? "#1e293b" : "#f8faff" }}>
            {options?.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
          </select>
        ) : type === "textarea" ? (
          <textarea value={value || ""} onChange={e => onChange(e.target.value)} rows={3} style={{ ...inp, resize: "vertical" }} />
        ) : (
          <input value={value || ""} onChange={e => onChange(e.target.value)} style={inp} />
        )
      ) : (
        <div style={{ fontSize: 13, fontWeight: 600, color: T.text, padding: "8px 0" }}>{value || "—"}</div>
      )}
    </div>
  );
}